import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, Newspaper, ArrowRight } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/blog?limit=30")
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="au-root">
      <header style={S.header}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Link to="/about" className="au-pill au-pill-ghost au-pill-sm">About</Link>
          <Link to="/" className="au-pill au-pill-glass au-pill-sm"><ArrowLeft /> Home</Link>
        </div>
      </header>

      <div className="au-container" style={S.container}>
        <div className="au-eyebrow" style={{ marginBottom: 14 }}>Blog</div>
        <h1 style={S.h1}>
          Security Insights &amp; <span style={{ color: "var(--au-accent-ink)" }}>Analysis</span>
        </h1>
        <p className="au-lead" style={{ marginBottom: 36 }}>
          Practical cybersecurity and privacy coverage — the latest threats, breaches, and defenses, explained.
        </p>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--au-muted)" }}>Loading articles…</div>
        ) : posts.length === 0 ? (
          <div className="au-glass au-pad" style={S.notice}>
            <Newspaper style={{ width: 20, height: 20, color: "var(--au-warn)", flex: "none", marginTop: 2 }} />
            <div>
              <strong style={{ color: "var(--au-warn)" }}>Fresh posts incoming.</strong> Our security blog is warming up —
              new articles publish here automatically. Check back soon.
            </div>
          </div>
        ) : (
          <div style={S.grid}>
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="au-glass au-lift" style={S.card}>
                {p.hero_image ? (
                  <div style={{ ...S.thumb, backgroundImage: `url(${p.hero_image})` }} />
                ) : (
                  <div style={{ ...S.thumb, ...S.thumbFallback }}><Newspaper size={28} style={{ opacity: 0.4 }} /></div>
                )}
                <div style={{ padding: 18, display: "flex", flexDirection: "column", flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="au-chip au-chip-cat au-cat-blue" style={{ fontSize: 10 }}>{t}</span>
                    ))}
                  </div>
                  <h2 style={S.postTitle}>{p.title}</h2>
                  <p style={S.postExcerpt}>{p.excerpt}</p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
                    <span style={{ fontSize: 11, color: "var(--au-muted)" }}>{fmtDate(p.published_at)}</span>
                    <span style={{ fontSize: 12, color: "var(--au-accent-ink)", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const S = {
  header: { maxWidth: 1100, margin: "0 auto", padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 },
  container: { maxWidth: 1100, padding: "40px 24px 60px" },
  h1: { fontSize: "clamp(2rem, 5vw, 3rem)", margin: "0 0 16px" },
  notice: { display: "flex", gap: 12, alignItems: "flex-start", fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.7 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 22 },
  card: { display: "flex", flexDirection: "column", overflow: "hidden", textDecoration: "none", padding: 0 },
  thumb: { height: 168, backgroundSize: "cover", backgroundPosition: "center", flex: "none" },
  thumbFallback: { display: "flex", alignItems: "center", justifyContent: "center", background: "var(--au-glass-strong)", color: "var(--au-accent-ink)" },
  postTitle: { fontSize: 18, fontWeight: 700, color: "var(--au-text)", margin: "0 0 10px", lineHeight: 1.35 },
  postExcerpt: { fontSize: 13, color: "var(--au-text-2)", lineHeight: 1.65, margin: "0 0 18px" },
};
